// Client-specific configuration for the Beacon Research machine-spend demo —
// the same templatization seam as apps/demo/src/scenario/torbridge.ts and
// apps/clinic-demo/src/scenario/veralta.ts. Components read from this object
// and never hard-code client names or narrative text.
//
// Beacon leads with a distinct LAYOUT — a spend statement / ledger (SpendLedger)
// whose every line is one recorded resource_purchased receipt — rather than the
// two-card verify hero the other demos use. The live seal sits behind a second
// tab, sharing the same LivePanel + gig catalog.

import type { LivePanelCopy } from "@demo/ui/LivePanel.js";
import type { StatementConfig } from "@demo/ui/SpendLedger.js";

export interface BeaconScenario {
  client: { name: string; domain: string; mark: string };
  product: { name: string; tagline: string };
  disclosure: string;
  masthead: string;
  intro: { headline: string; body: string };
  // Overview tab: the spend statement / ledger. Row bundleIds key into
  // BEACON_BUNDLES (apps/spend-demo/src/data.ts).
  statement: StatementConfig;
  liveCta: { label: string; sub: string };
  live: LivePanelCopy;
}

export const BEACON: BeaconScenario = {
  client: {
    name: "Beacon Research",
    domain: "beaconresearchagent.com",
    mark: "B"
  },
  product: {
    name: "Sequesign",
    tagline: "Verifiable receipts for agent actions"
  },
  disclosure:
    "Fictional demonstration. Beacon Research and the purchases shown are not real. Each line is a real Sequesign receipt, sealed by an in-process demo witness and verified live in your browser. Nothing is sent anywhere.",
  masthead: "Metered spend, verified",
  statement: {
    eyebrow: "See it in 15 seconds",
    headline:
      "An agent bought resources to finish a task, on a budget. Trust the statement, or verify every line.",
    sub: "To complete a research brief, Beacon's agent buys the paid resources it needs: search APIs, licensed data, compute, bounded by a spend mandate. Every purchase is sealed into a receipt. One went over the per-purchase cap; the statement records it, doesn't hide it. Verify any line right here in your browser:",
    title: "Spend statement: “Q3 competitive landscape” research task",
    subtitle: "mandate bounded_spend_mandate · 2026-08-14",
    mandate: {
      capLabel: "Per-purchase cap",
      cap: "$500.00",
      allowedLabel: "Allowed",
      allowed: "search_api · licensed_data · compute",
      budgetLabel: "Task budget used",
      budget: "$1,806 / $2,500",
      budgetFraction: 1806 / 2500
    },
    rows: [
      {
        bundleId: "purchase-search-api",
        resource: "Lumen Search API: 1,200 queries",
        category: "search_api",
        amount: "$96.00",
        expectConformant: true
      },
      {
        bundleId: "purchase-licensed-data",
        resource: "Cardinal Markets: market dataset pull",
        category: "licensed_data",
        amount: "$320.00",
        expectConformant: true
      },
      {
        bundleId: "purchase-compute",
        resource: "Nimbus Compute: H100 GPU, 6.5 hrs",
        category: "compute",
        amount: "$390.00",
        expectConformant: true
      },
      {
        bundleId: "purchase-over-cap",
        resource: "Argus Capital Data: full license seat",
        category: "licensed_data",
        amount: "$1,000.00",
        expectConformant: false,
        overCapNote: "over $500 cap"
      }
    ],
    verifyLabel: "Verify",
    verifyingLabel: "Verifying…",
    verifyAllLabel: "Verify all lines",
    footNote:
      "Each line is a real sealed receipt, recomputed in your browser with the real Sequesign verifier, nothing sent anywhere.",
    browserNote:
      "The over-cap line still verifies as authentic. It honestly records conformant: false. The mandate caught the overspend; the receipt didn't hide it."
  },
  liveCta: {
    label: "Run a real seal with your own keys",
    sub: "Presenter mode: seal a brand-new purchase against the real witness, end to end, and verify it here."
  },
  intro: {
    headline: "Every agent purchase, provable.",
    body: "Beacon's research agent buys the paid resources it needs to finish a task, under a bounded spend mandate: a per-purchase cap and a set of allowed categories. Enter a purchase and run it: the agent seals a real Sequesign receipt server-side, then you verify it right here in your browser. Push a purchase past the cap, or into an unbudgeted category, and the sealed receipt honestly records conformant: false. It still verifies as authentic; the violation is recorded, not hidden."
  },
  live: {
    title: "Live run: real seal, real witness",
    intro: [
      "Enter a purchase and run it: the agent executes a real Sequesign session with your own agent key, gets the action countersigned by a real witness, and seals the package, then verifies it here in your browser.",
      "The result is verified in EXTERNAL trust-anchor mode: the witness's published keys are fetched from its well-known endpoint (directly, or relayed unchanged through this site) and used as the trust anchor, so this is an independent third-party check, not an integrity self-check.",
      "The spend mandate binds a $500 per-purchase cap and a set of allowed categories (search_api, licensed_data, compute). Push a purchase over the cap, or pick the off-allowlist “consulting” category, and the sealed receipt honestly records conformant: false. It still verifies as authentic; the violation is recorded, not hidden."
    ],
    contactNote:
      "Live mode contacts the real witness using the keys you enter below. Nothing else on this page makes a network call to that service.",
    keyNote:
      "Keys are used only for this run, in your browser and the request; nothing is stored. They live in React state only, are never written to localStorage, and are cleared when you leave the page.",
    runLabel: "Seal the purchase",
    runningLabel: "Sealing…",
    fields: {
      agentPem: "Agent private key (Ed25519 PEM)",
      witnessBaseUrl: "Witness base URL",
      witnessApiKey: "Witness API key (optional)",
      llmApiKey: "Anthropic API key"
    }
  }
};
